import { unref } from 'vue';

import type { BvEmptyStateProps } from '@bv/components/empty-state/BvEmptyState.vue';
import { usePermission } from '@bv/composables/auth/permission';
import type { PlaceholderStateMeta, PlaceholderStateMetaBase, RouteMeta } from '@bv/router';
import { useUserStore } from '@bv/stores/user';
import { evaluateVisibility } from '@bv/utils/common/evaluate-visibility';

type RouteAccessMeta = Pick<RouteMeta, 'canAccess' | 'permissions' | 'placeholderState'>;

export type ResolvedRoutePlaceholderState = {
  cardTitle?: string;
  props: BvEmptyStateProps;
};

/**
 * Resolves route `placeholderState` meta through MaybeRef and optional factory function forms.
 */
export const resolvePlaceholderStateMeta = (
  placeholderState: PlaceholderStateMeta | undefined,
): PlaceholderStateMetaBase | undefined => {
  let resolved = unref(placeholderState);
  if (!resolved) {
    return undefined;
  }

  if (typeof resolved === 'function') {
    resolved = unref(resolved());
  }

  return resolved || undefined;
};

/**
 * Whether the layout will render the placeholder state for this route meta.
 * Used for soft-allow in the navigation guard so it matches layout behavior.
 */
export const willRenderRoutePlaceholderState = async (meta: RouteAccessMeta): Promise<boolean> => {
  const resolved = resolvePlaceholderStateMeta(meta.placeholderState);
  if (!resolved) {
    return false;
  }

  return !resolved.onlyWhenInaccessible || !(await evaluateRouteMetaAccessAsync(meta));
};

/**
 * Resolves placeholder-state props for the layout to render, or `undefined` to hide it.
 * Honors `onlyWhenInaccessible` against the route's access checks.
 * Layout-only keys (`cardTitle`) are split from `BvEmptyState` props.
 */
export const resolveRoutePlaceholderState = (meta: RouteAccessMeta): ResolvedRoutePlaceholderState | undefined => {
  const resolved = resolvePlaceholderStateMeta(meta.placeholderState);
  if (!resolved) {
    return undefined;
  }

  const { onlyWhenInaccessible, cardTitle, ...props } = resolved;
  if (onlyWhenInaccessible && evaluateRouteMetaAccess(meta)) {
    return undefined;
  }

  return { cardTitle, props };
};

const evaluateRouteMetaAccessWith = (
  meta: RouteAccessMeta,
  checkPermissions: (permissions: string | string[]) => boolean | Promise<boolean>,
): boolean | Promise<boolean> => {
  if (meta.canAccess !== undefined && !evaluateVisibility(meta.canAccess)) {
    return false;
  }

  if (meta.permissions) {
    return checkPermissions(meta.permissions);
  }

  return true;
};

/**
 * Synchronously evaluates whether the current user passes a route's `canAccess`
 * and `permissions` checks. Assumes user details are already loaded.
 */
export const evaluateRouteMetaAccess = (meta: RouteAccessMeta): boolean => {
  return evaluateRouteMetaAccessWith(
    meta,
    (permissions) => usePermission().hasPermission(permissions).value,
  ) as boolean;
};

/**
 * Async variant that ensures user details are loaded before checking permissions.
 */
export const evaluateRouteMetaAccessAsync = async (meta: RouteAccessMeta): Promise<boolean> => {
  return await evaluateRouteMetaAccessWith(meta, async (permissions) => {
    await useUserStore().getUserDetails();
    return usePermission().hasPermission(permissions).value;
  });
};
